import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@components/ui';
import { Loader2, Sparkles } from 'lucide-react';

interface CustomPromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (title: string, prompt: string) => void;
  isSubmitting: boolean;
}

export const CustomPromptDialog: React.FC<CustomPromptDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');

  const handleSubmit = () => {
    onSubmit(title, prompt);
    setTitle('');
    setPrompt('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Create Custom Prompt</DialogTitle>
          <DialogDescription>
            Create a custom prompt to repurpose your content in a unique way.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="title" className="text-sm font-medium">
              Prompt Title
            </label>
            <input
              id="title"
              type="text"
              placeholder="e.g., LinkedIn Article"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid gap-2">
            <label htmlFor="prompt" className="text-sm font-medium">
              Prompt Text
            </label>
            <textarea
              id="prompt"
              placeholder="Write your custom prompt here. Use {content} where you want the original content to be inserted."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <p className="text-xs text-gray-500">
              Tip: Use {`{content}`} to reference the original content in your prompt.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!title.trim() || !prompt.trim() || isSubmitting}
            className="bg-black text-white hover:bg-gray-800"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Create & Generate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
